/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  oneOfType, arrayOf, node, string, shape
} from 'prop-types';
import { Heading } from '@thd-olt-component-react/core-ui';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { Spotlight } from '@thd-olt-component-react/spotlight';
import { QueryProvider } from '@thd-nucleus/data-sources';

import classnames from 'classnames/bind';
import sectionStyles from './section.module.scss';

const styles = classnames.bind(sectionStyles);
/*
persistedOffers is required to ensure I don't keep making API calls if I have already fetched data. useRef would usually be used for something like this but even useRef was not maintaining it's state as I resized the browser.
*/
const persistedOffers = {
  requested: false,
  data: null
};

const shouldFetchOffers = (offers, mcvisID) => {
  if (mcvisID && !offers?.requested && !offers?.data?.length) {
    return true;
  }
  return false;
};

// The purpose of this whole file is for the marketing offers a/b test
// See https://confluence.homedepot.com/x/AgYYFQ
const MarketingOffers = (props) => {
  const {
    components,
    title,
    mcvisID,
    storeId
  } = props;

  const [offers, setOffers] = useState(components);
  const { enabled } = useLayoutGroupProps({ props });

  const fetchTestOffers = async () => {  // eslint-disable-line
    // Use my mcvisID: 29300599488836994130110129827609800860 to debug this locally.
    persistedOffers.requested = true;

    try {
      // eslint-disable-next-line max-len
      const response = await axios.get(`${window?.location.origin}/dts/v1/data?anchor=${mcvisID}&model=personalized_deals_s&appid=desktop&key=AnPrMR0LKfdglJQfoHqsT4sAhDLHNaGw&storeid=${storeId}`);

      const offersData = response?.data?.data;
      if (!offersData) {
        return null;
      }

      setOffers(offersData);
      persistedOffers.data = offersData;
    } catch (error) {
      persistedOffers.data = null;
    }
  };

  useEffect(() => {
    /*
    Re run this use effect if the feature switch prop "enabled" is toggled.

    Don't run the fetchTestOffers function if there is no mcvisID. It will avoid having 404 errors reported in the console.
    */
    if (enabled && shouldFetchOffers(persistedOffers, mcvisID)) {
      fetchTestOffers();
    } else if (enabled && persistedOffers?.data?.length) {
      setOffers(persistedOffers?.data);
    }
  }, [enabled]);

  /*
  The marketing cards should almost always enter this condition statement. The reason is to avoid the following react error.

  Warning: render(...): Replacing React-rendered children with a new root component.

  If there is no offers data coming from the API or the request fails for whatever reason, I am just passing the normal spotlight data directly into the "a/b test Spotlight".
  */
  if (offers?.length) {
    return (
      <>
        {title && (
          <Heading title={title} className={styles('section--title')} underline />
        )}
        <QueryProvider key="marketing-offers-test" cacheKey="marketing-offers-test">
          <div className={styles('section__container')}>{offers?.map((data, index) => {
            return (
              <Spotlight
                key={data?.score}
                testData={data}
                testEnabled
                showDescription
                boldTitle
                componentId={components[index]?.id}
                componentClass="Spotlight"
              />
            );
          })}
          </div>
        </QueryProvider>
      </>
    );
  }

  /*
  In some extreme case where there is no a/b test data coming from the API or component data coming from Contentful, return null. At this point the whole marketing gallery probably won't be working even if it was running the default Section logic.
  */
  return null;
};

MarketingOffers.propTypes = {
  children: oneOfType([node, arrayOf(node)]), // eslint-disable-line
  components: arrayOf(shape),
  mcvisID: string,
  storeId: string,
  title: string,
};

MarketingOffers.defaultProps = {
  children: null,
  components: null,
  mcvisID: null,
  storeId: null,
  title: null,
};

export { MarketingOffers };
